import { z } from "zod";
import { apiIntIdSchema } from "./ids.js";
/** Derived connection state for the Facebook settings UI. */
export const facebookConnectionStateSchema = z.enum([
    "NOT_CONNECTED",
    "PENDING_PAGE_SELECTION",
    "CONNECTED",
    "DISCONNECTED",
    "ERROR",
]);
export const facebookPageCandidateSchema = z.object({
    pageId: z.string().min(1),
    pageName: z.string().min(1),
});
export const facebookConnectedPageSchema = z.object({
    id: apiIntIdSchema,
    platformEndpointId: apiIntIdSchema,
    pageId: z.string().min(1),
    pageName: z.string().min(1),
    /** Customer-editable name on the endpoint. Null when unset. */
    localName: z.string().nullable(),
});
export const facebookConnectionPermissionsSchema = z.object({
    canConnect: z.boolean(),
    canDisconnect: z.boolean(),
    canSelectPage: z.boolean(),
    canCancelPending: z.boolean(),
});
export const facebookConnectConfigResponseSchema = z.object({
    enabled: z.boolean(),
    metaAppId: z.string().min(1),
    facebookLoginConfigId: z.string().min(1),
    graphApiVersion: z.string().min(1),
    hasExistingConnection: z.boolean(),
    connectionState: facebookConnectionStateSchema,
});
export const facebookConnectionResponseSchema = z.object({
    state: facebookConnectionStateSchema,
    platformAccountId: apiIntIdSchema.nullable(),
    page: facebookConnectedPageSchema.nullable(),
    /** Present only while a valid pending OAuth cookie exists for this organization. */
    pageCandidates: z.array(facebookPageCandidateSchema).optional(),
    permissions: facebookConnectionPermissionsSchema,
});
/**
 * Facebook OAuth start is a first-party GET on the API (no JSON body):
 *
 * `GET /organizations/:organizationId/facebook/connect/start`
 *
 * The route authenticates the Session cookie, requires Owner/Admin, sets the
 * encrypted HttpOnly `multi_chat_facebook_oauth_start` cookie on the API host,
 * and responds with HTTP 302 to Meta Facebook Login for Business.
 *
 * Frontend (`feat/facebook-connection-web-v1`): open this API URL directly via
 * `window.open` (popup). Do not XHR/fetch a Meta authorization URL first — that
 * cross-origin cookie bootstrap fails in production.
 *
 * Expected popup chain: Web → API start (Set-Cookie + 302) → Meta → API callback.
 * That top-level navigation chain must preserve `window.opener` so the existing
 * callback HTML can `postMessage` to the Multi-Chat web origin.
 */
export const selectFacebookPageRequestSchema = z.object({
    pageId: z.string().min(1),
});
export const selectFacebookPageResponseSchema = facebookConnectionResponseSchema;
export const disconnectFacebookResponseSchema = facebookConnectionResponseSchema;
export const cancelPendingFacebookResponseSchema = facebookConnectionResponseSchema;
/**
 * Safe postMessage payloads from the Facebook OAuth popup callback.
 * Never include Session tokens, Facebook tokens, codes, or provider error text.
 */
export const FACEBOOK_OAUTH_SUCCESS_TYPE = "MULTI_CHAT_FACEBOOK_OAUTH_SUCCESS";
export const FACEBOOK_OAUTH_ERROR_TYPE = "MULTI_CHAT_FACEBOOK_OAUTH_ERROR";
export const facebookOauthSuccessMessageSchema = z.object({
    type: z.literal(FACEBOOK_OAUTH_SUCCESS_TYPE),
});
export const facebookOauthErrorMessageSchema = z.object({
    type: z.literal(FACEBOOK_OAUTH_ERROR_TYPE),
    code: z.string().min(1),
});
export const facebookOauthPopupMessageSchema = z.union([
    facebookOauthSuccessMessageSchema,
    facebookOauthErrorMessageSchema,
]);
//# sourceMappingURL=facebook-contracts.js.map